import React, { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';
import dashjs from 'dashjs';
import { useDispatch, useSelector } from 'react-redux';
import logger from '../../utils/logger';
import { 
  Volume2, VolumeX, 
  Maximize, AlertCircle, Signal,
  Play, Pause,
  PictureInPicture2,
  Subtitles, X,
  SkipForward,
  SkipBack
} from 'lucide-react';
import { 
  setFullscreen, 
  setSubtitlesEnabled, 
  setCurrentSubtitle, 
  setAvailableSubtitles,
  setAvailableQualities,
  setPlaybackSpeed,
  setSettingsOpen,
  setSelectedQuality,
  setAutoQuality,
  setCurrentActiveQuality,
  setSeekingState,
} from '../../store/slices/playerSlice';
import { RootState } from '../../store';
import { Settings } from './Settings';
import clsx from 'clsx';

interface HTMLVideoElementWithWebkit extends HTMLVideoElement {
  webkitEnterFullscreen?: () => void;
  webkitSupportsFullscreen?: boolean;
}

interface VideoPlayerProps {
  url: string;
}

interface StreamStats {
  bitrate: number;
  buffered: number;
  dropped: number;
  level: string;
}

export const VideoPlayer: React.FC<VideoPlayerProps> = ({ url }) => {
  const videoRef = useRef<HTMLVideoElementWithWebkit>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [isHovering, setIsHovering] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(false);
  const [isPiP, setIsPiP] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [stats, setStats] = useState<StreamStats>({
    bitrate: 0,
    buffered: 0,
    dropped: 0,
    level: ''
  });
  const hlsRef = useRef<Hls | null>(null);
  const dashRef = useRef<dashjs.MediaPlayerClass | null>(null);
  const dispatch = useDispatch();
  const {
    isFullscreen,
    subtitlesEnabled,
    playbackSpeed,
    settingsOpen,
    selectedQuality,
    autoQuality,
    isSeeking,
  } = useSelector((state: RootState) => state.player);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const lastClickTime = useRef<number>(0);
  const seekDebounceRef = useRef<NodeJS.Timeout | null>(null);

  // Add timer ref to track single instance
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Add tap indicator state
  const [tapIndicator, setTapIndicator] = useState<{ x: number; y: number } | null>(null);

  // Detect if device is mobile
  const isMobile = !window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  // Add seeking timeout ref
  const seekingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Add timeouts reference
  const timeoutRefs = useRef<number[]>([]);

  // Add debug log for initial stats
  useEffect(() => {
    logger.debug('Initial stats state:', stats);
  }, []);

  // Add effect to log stats changes
  useEffect(() => {
    logger.debug('Stats updated:', stats);
  }, [stats]);

  // Consolidated timer management
  const handleMouseMove = () => {
    if (!isMobile) {
      if (!isFullscreen && !settingsOpen) return;
      
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      setControlsVisible(true);

      // Only set timer if settings are closed
      if (!settingsOpen) {
        timerRef.current = setTimeout(() => {
          if (!settingsOpen) {
            setControlsVisible(false);
          }
        }, 2000);
      }
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      timeoutRefs.current.forEach(timeout => {
        window.clearTimeout(timeout);
      });
      timeoutRefs.current = [];
    };
  }, []);

  // Initialize player when URL changes
  useEffect(() => {
    if (!videoRef.current || !url) return;

    const video = videoRef.current;
    video.volume = volume;
    video.muted = isMuted;

    setIsLoading(true);
    setIsVideoReady(false);
    setError(null);

    // Add event listeners for loading states
    const handleLoadStart = () => {
      setIsLoading(true);
      setIsVideoReady(false);
      setIsPlaying(false);
    };

    const handleLoadedData = () => {
      setIsVideoReady(true);
    };

    const handleCanPlay = () => {
      setIsVideoReady(true);
    };

    const handlePlaying = () => {
      setIsPlaying(true);
      // Allow playback to begin while still showing loading
      const video = videoRef.current;
      if (video && !video.paused && !video.ended) {
        // Use a short delay to ensure the video has actually started
        setTimeout(() => {
          if (video && !video.paused && !video.ended) {
            setIsLoading(false);
          }
        }, 500);
      }
    };

    const handleWaiting = () => {
      if (!isPlaying) {
        setIsLoading(true);
      }
    };

    const handleStalled = () => {
      setIsLoading(true);
    };

    const handlePause = () => {
      setIsPlaying(false);
    };

    const handleError = () => {
      setIsLoading(false);
      setError('Failed to load video - please try another stream');
    };

    video.addEventListener('loadstart', handleLoadStart);
    video.addEventListener('loadeddata', handleLoadedData);
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('playing', handlePlaying);
    video.addEventListener('waiting', handleWaiting);
    video.addEventListener('stalled', handleStalled);
    video.addEventListener('pause', handlePause);
    video.addEventListener('error', handleError);

    // Clean up function to properly destroy players
    const cleanupPlayers = () => {
      try {
        video.removeEventListener('loadstart', handleLoadStart);
        video.removeEventListener('loadeddata', handleLoadedData);
        video.removeEventListener('canplay', handleCanPlay);
        video.removeEventListener('playing', handlePlaying);
        video.removeEventListener('waiting', handleWaiting);
        video.removeEventListener('stalled', handleStalled);
        video.removeEventListener('pause', handlePause);
        video.removeEventListener('error', handleError);
        
        if (hlsRef.current) {
          hlsRef.current.stopLoad();
          hlsRef.current.detachMedia();
          hlsRef.current.destroy();
          hlsRef.current = null;
        }
        if (dashRef.current) {
          dashRef.current.reset();
          dashRef.current.destroy();
          dashRef.current = null;
        }
        if (videoRef.current) {
          videoRef.current.removeAttribute('src');
          videoRef.current.load();
        }
      } catch (e) {
        logger.error('Cleanup error:', e);
      }
    };

    // Clean up before initializing new player
    cleanupPlayers();

    const initPlayer = async () => {
      try {
        logger.debug('Initializing player:', {
          url,
          isHLS: url.includes('.m3u8') || url.includes('playlist.m3u'),
          isDASH: url.includes('.mpd'),
          hlsSupported: Hls.isSupported()
        });

        // Check if URL is HTTP and we're on HTTPS
        if (url.startsWith('http:') && window.location.protocol === 'https:') {
          setError('HTTP stream cannot be played on HTTPS website. Please use an HTTPS stream URL.');
          setIsLoading(false);
          return;
        }

        if (url.includes('.mpd')) {
          const player = dashjs.MediaPlayer().create();
          dashRef.current = player;
          
          player.initialize();
          player.attachView(video);
          player.attachSource(url);

          logger.debug('DASH player initialized:', {
            url,
            bufferLength: player.getBufferLength('video')
          });

          player.on(dashjs.MediaPlayer.events.ERROR, (error: any) => {
            logger.error('DASH playback error:', error);
            setError('Playback error - attempting to recover...');
            try {
              player.reset();
              player.attachView(video);
              player.attachSource(url);
            } catch (e) {
              logger.error('DASH recovery failed:', e);
              setError('Failed to play video - please try another stream');
              setIsLoading(false);
              setIsVideoReady(false);
            }
          });

          player.on(dashjs.MediaPlayer.events.PLAYBACK_PLAYING, () => {
            setIsPlaying(true);
            // Allow playback to begin while still showing loading
            const video = videoRef.current;
            if (video && !video.paused && !video.ended) {
              // Use a short delay to ensure the video has actually started
              setTimeout(() => {
                if (video && !video.paused && !video.ended) {
                  setIsLoading(false);
                }
              }, 500);
            }
          });

          player.on(dashjs.MediaPlayer.events.PLAYBACK_WAITING, () => {
            if (!isPlaying) {
              setIsLoading(true);
            }
          });

          player.on(dashjs.MediaPlayer.events.PLAYBACK_PAUSED, () => {
            setIsPlaying(false);
          });

          // Add timeout for initial load
          const loadTimeout = setTimeout(() => {
            if (!player.isReady()) {
              logger.error('DASH stream load timeout:', {
                bufferLength: player.getBufferLength('video'),
                readyState: video.readyState,
                networkState: video.networkState,
                error: video.error
              });
              setError('Stream load timeout - please try another stream');
              setIsLoading(false);
              setIsVideoReady(false);
              player.reset();
            }
          }, 10000);

          player.on(dashjs.MediaPlayer.events.QUALITY_CHANGE_RENDERED, (e: any) => {
            const qualities = player.getBitrateInfoListFor("video");
            const currentQuality = qualities[player.getQualityFor("video")];
            
            logger.debug('DASH quality changed:', {
              qualities: qualities.map(q => ({
                height: q.height,
                width: q.width,
                bitrate: q.bitrate
              })),
              currentQuality: {
                height: currentQuality.height,
                width: currentQuality.width,
                bitrate: currentQuality.bitrate
              }
            });

            // Update available qualities if not set
            const availableQualities = qualities.map(info => `${info.height}p`).filter(Boolean);
            if (availableQualities.length > 0) {
              dispatch(setAvailableQualities(availableQualities));
            }

            // Update current active quality
            dispatch(setCurrentActiveQuality(`${currentQuality.height}p`));

            // Add this log to track stats updates
            logger.debug('Updating stats with new DASH bitrate:', currentQuality.bitrate / 1000);
            
            setStats(prev => ({
              ...prev,
              bitrate: currentQuality.bitrate / 1000,
              level: `${player.getQualityFor("video")}`
            }));
          });

          player.on(dashjs.MediaPlayer.events.TEXT_TRACKS_ADDED, () => {
            const tracks = player.getTracksFor('text');
            if (tracks && tracks.length > 0) {
              dispatch(setAvailableSubtitles(tracks.map((track: any) => ({
                id: track.id,
                language: track.lang || 'unknown',
                label: track.labels?.[0]?.text || track.lang || 'Unknown',
                mode: track.mode || 'disabled'
              }))));
            }
          });

          player.updateSettings({
            streaming: {
              buffer: {
                fastSwitchEnabled: true,
                bufferTimeAtTopQuality: 30,
                bufferTimeAtTopQualityLongForm: 60
              },
              abr: {
                useDefaultABRRules: true,
                autoSwitchBitrate: {
                  video: true,
                  audio: true
                }
              }
            }
          });

          player.on(dashjs.MediaPlayer.events.CAN_PLAY, () => {
            clearTimeout(loadTimeout);
            setIsLoading(false);
            video.play().catch(error => {
              logger.error('DASH play error:', error);
              setError('Failed to start playback - please try another stream');
            });
          });
        }
        else if (Hls.isSupported() && (url.includes('.m3u8') || url.includes('playlist.m3u'))) {
          const hls = new Hls({
            enableWorker: true,
            debug: false,
            maxBufferLength: 60,
            maxMaxBufferLength: 600,
            maxBufferSize: 60 * 1000 * 1000,
            maxBufferHole: 0.5,
            highBufferWatchdogPeriod: 2,
            nudgeOffset: 0.1,
            nudgeMaxRetry: 5,
            maxFragLookUpTolerance: 0.25,
            liveSyncDurationCount: 3,
            liveMaxLatencyDurationCount: 10,
            enableSoftwareAES: true,
            manifestLoadingTimeOut: 30000,
            manifestLoadingMaxRetry: 4,
            manifestLoadingRetryDelay: 2000,
            levelLoadingTimeOut: 20000,
            levelLoadingMaxRetry: 4,
            levelLoadingRetryDelay: 2000,
            fragLoadingTimeOut: 30000,
            fragLoadingMaxRetry: 6,
            fragLoadingRetryDelay: 2000,
            startFragPrefetch: true,
            testBandwidth: true,
            progressive: true,
            lowLatencyMode: true,
            capLevelToPlayerSize: true,
            startLevel: -1,
            abrEwmaDefaultEstimate: 500000,
            abrEwmaFastLive: 3,
            abrEwmaSlowLive: 9,
          });

          hlsRef.current = hls;
          let loadTimeout: number | undefined;
          let retryTimeout: number | undefined;

          const clearTimeouts = () => {
            if (loadTimeout) {
              logger.debug('Clearing initial load timeout');
              window.clearTimeout(loadTimeout);
              loadTimeout = undefined;
            }
            if (retryTimeout) {
              logger.debug('Clearing retry timeout');
              window.clearTimeout(retryTimeout);
              retryTimeout = undefined;
            }
          };

          // Clear timeouts on cleanup
          const cleanup = () => {
            clearTimeouts();
            if (hlsRef.current) {
              hlsRef.current.destroy();
              hlsRef.current = null;
            }
          };

          hls.on(Hls.Events.MEDIA_ATTACHED, () => {
            logger.debug('HLS: Media attached, starting load timeout');
            
            // Set initial load timeout
            loadTimeout = window.setTimeout(() => {
              if (hlsRef.current && !isVideoReady) {
                logger.warn('Initial load timeout - attempting retry', {
                  videoState: {
                    readyState: video.readyState,
                    networkState: video.networkState,
                    error: video.error,
                    currentTime: video.currentTime,
                    duration: video.duration,
                    paused: video.paused
                  },
                  hlsState: {
                    levels: hls.levels,
                    currentLevel: hls.currentLevel,
                    loadLevel: hls.loadLevel
                  }
                });
                
                // Try to recover by reloading
                hls.loadSource(url);
                
                // Set retry timeout
                retryTimeout = window.setTimeout(() => {
                  if (hlsRef.current && !isVideoReady) {
                    logger.error('Stream load retry timeout - final', {
                      videoState: {
                        readyState: video.readyState,
                        networkState: video.networkState,
                        error: video.error
                      },
                      hlsState: {
                        levels: hls.levels,
                        currentLevel: hls.currentLevel,
                        loadLevel: hls.loadLevel
                      }
                    });
                    cleanup();
                    setError('Stream load timeout - please try another stream');
                    setIsLoading(false);
                  }
                }, 10000); // 10 second retry window

                timeoutRefs.current.push(retryTimeout);
              }
            }, 20000); // 20 second initial window

            timeoutRefs.current.push(loadTimeout);
            hls.loadSource(url);
          });

          hls.on(Hls.Events.MANIFEST_LOADING, () => {
            logger.debug('HLS: Manifest loading started');
          });

          hls.on(Hls.Events.MANIFEST_LOADED, (event, data) => {
            logger.debug('HLS: Manifest loaded:', {
              url: data.url,
              levels: data.levels.length
            });
          });

          hls.on(Hls.Events.MANIFEST_PARSED, (event, data) => {
            logger.debug('HLS: Manifest parsed successfully:', {
              levels: data.levels.length,
              firstLevel: data.firstLevel,
              stats: {
                loading: hls.loadLevel,
                current: hls.currentLevel,
                levelCount: hls.levels.length
              }
            });
            
            // Clear timeouts on successful parse
            clearTimeouts();
            
            // Set available qualities from manifest
            const qualities = data.levels.map(level => 
              `${level.height}p`
            ).filter(Boolean); // Filter out any undefined/null values
            
            if (qualities.length > 0) {
              logger.debug('Setting available qualities from HLS manifest:', qualities);
              dispatch(setAvailableQualities(qualities));
              // Start with auto quality
              dispatch(setSelectedQuality('auto'));
              dispatch(setAutoQuality(true));
            } else {
              logger.warn('No quality levels found in HLS manifest');
            }

            setIsVideoReady(true);
            
            if (videoRef.current) {
              videoRef.current.play()
                .then(() => {
                  logger.debug('Playback started successfully');
                  setIsLoading(false);
                })
                .catch(error => {
                  if (error.name !== 'AbortError') {
                    logger.error('Play error:', error);
                    setError('Playback failed - please try another stream');
                    setIsLoading(false);
                  } else {
                    logger.debug('Play request aborted - new load request incoming');
                  }
                });
            }
          });

          // Add fragment loading progress monitoring
          hls.on(Hls.Events.FRAG_LOADING, () => {
            if (!isVideoReady) {
              logger.debug('HLS: Fragment loading started');
            }
          });

          hls.on(Hls.Events.FRAG_LOADED, () => {
            if (!isVideoReady) {
              logger.debug('HLS: Fragment loaded successfully');
              setIsVideoReady(true);
              clearTimeouts(); // Clear timeouts when first fragment loads
            }
          });

          // Monitor level switches for quality updates
          hls.on(Hls.Events.LEVEL_SWITCHED, (event: any, data: any) => {
            const newLevel = hls.levels[data.level];
            if (newLevel) {
              dispatch(setCurrentActiveQuality(`${newLevel.height}p`));
              logger.debug('HLS quality switched:', {
                newQuality: `${newLevel.height}p`,
                bitrate: newLevel.bitrate,
                level: data.level
              });
              
              // Update stats with the new bitrate - fix by dividing by 1000 to get kbps
              setStats(prev => ({
                ...prev,
                bitrate: newLevel.bitrate / 1000,
                level: `${data.level}`
              }));
            }
          });

          hls.on(Hls.Events.ERROR, (event, data) => {
            if (data.fatal) {
              logger.error('HLS fatal error:', {
                type: data.type,
                details: data.details,
                error: data.error,
                fatal: data.fatal
              });
              // Handle specific HTTP errors
              const responseCode = data.response?.code;
              if (responseCode === 404) {
                setError('Stream URL not found (404) - The stream may be offline or removed');
                setIsLoading(false);
                return;
              } else if (responseCode && responseCode >= 400) {
                setError(`Stream error (${responseCode}) - Please try another stream`);
                setIsLoading(false);
                return;
              }
              
              setError('Stream connection interrupted, attempting to reconnect...');
              setTimeout(() => {
                if (hlsRef.current) {
                  hlsRef.current.startLoad();
                  // Clear error after successful recovery
                  setTimeout(() => {
                    if (videoRef.current && !videoRef.current.paused) {
                      setError(null);
                    }
                  }, 2000);
                }
              }, 1000);
            } else if (data.details === 'fragLoadError') {
              logger.error('Fragment load error:', data);
              if (data.response?.code === 404) {
                setError('Stream fragments not found - The stream may be broken');
              }
              if (videoRef.current && !videoRef.current.paused) {
                setIsLoading(false);
              }
            }
          });

          try {
            logger.debug('Attaching HLS media');
            hls.attachMedia(video);
          } catch (e) {
            logger.error('Media attachment failed:', e);
            cleanup();
            setError('Failed to initialize player - please try another stream');
          }
        }
        else {
          video.src = url;
          video.load();
          
          const loadTimeout = setTimeout(() => {
            if (!video.readyState) {
              setError('Stream load timeout - please try another stream');
              video.src = '';
            }
          }, 10000);
          
          video.addEventListener('canplay', () => {
            clearTimeout(loadTimeout);
            setIsLoading(false);
            video.play().catch(error => {
              logger.error('Direct play error:', error);
              setError('Failed to start playback - please try another stream');
            });
          }, { once: true });

          video.addEventListener('error', () => {
            setError('Failed to load video - please try another stream');
          }, { once: true });
        }
      } catch (err) {
        logger.error('Player initialization error:', err);
        cleanupPlayers();
        setError('Failed to initialize video player');
        setIsLoading(false);
      }
    };

    initPlayer();

    // Return cleanup function
    return cleanupPlayers;
  }, [url]);

  // Handle volume and mute changes separately
  useEffect(() => {
    if (!videoRef.current) return;
    videoRef.current.volume = volume;
    videoRef.current.muted = isMuted;
  }, [volume, isMuted]);

  // Handle video events
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handlePlay = () => {
      setIsPlaying(true);
    };

    const handlePause = () => {
      setIsPlaying(false);
    };

    const handleEnded = () => {
      setIsPlaying(false);
    };

    const handleVolumeChange = () => {
      setVolume(video.volume);
      setIsMuted(video.muted);
    };

    const handleEnterpictureinpicture = () => {
      setIsPiP(true);
    };

    const handleLeavepictureinpicture = () => {
      setIsPiP(false);
    };

    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('volumechange', handleVolumeChange);
    video.addEventListener('enterpictureinpicture', handleEnterpictureinpicture);
    video.addEventListener('leavepictureinpicture', handleLeavepictureinpicture);

    return () => {
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('volumechange', handleVolumeChange);
      video.removeEventListener('enterpictureinpicture', handleEnterpictureinpicture);
      video.removeEventListener('leavepictureinpicture', handleLeavepictureinpicture);
    };
  }, []);

  const togglePlay = () => {
    if (!videoRef.current) return;
    
    // Toggle play state
    if (videoRef.current.paused) {
      videoRef.current.play().catch(console.error);
    } else {
      videoRef.current.pause();
    }
  };

  const toggleMute = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!videoRef.current) return;
    videoRef.current.muted = !videoRef.current.muted;
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (!videoRef.current) return;
    const newVolume = parseFloat(e.target.value);
    videoRef.current.volume = newVolume;
  };

  const toggleFullscreen = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!videoRef.current) return;

    // Check if running on iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    if (isIOS) {
      // For iOS, we use the video element's webkitEnterFullscreen
      if (videoRef.current.webkitEnterFullscreen) {
        videoRef.current.webkitEnterFullscreen();
      }
    } else {
      // For other browsers, use the standard Fullscreen API
      if (!document.fullscreenElement) {
        containerRef.current?.requestFullscreen().then(() => {
          dispatch(setFullscreen(true));
        }).catch(console.error);
      } else {
        document.exitFullscreen().then(() => {
          dispatch(setFullscreen(false));
        }).catch(console.error);
      }
    }
  };

  const togglePiP = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!videoRef.current) return;
    if (document.pictureInPictureElement) {
      document.exitPictureInPicture().catch(console.error);
    } else if (document.pictureInPictureEnabled) {
      videoRef.current.requestPictureInPicture().catch(console.error);
    }
  };

  const handleSubtitleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!videoRef.current) return;

    const video = videoRef.current;
    const tracks = video.textTracks;

    if (subtitlesEnabled) {
      // Disable subtitles
      dispatch(setSubtitlesEnabled(false));
      dispatch(setCurrentSubtitle(null));
      if (tracks) {
        for (let i = 0; i < tracks.length; i++) {
          tracks[i].mode = 'disabled';
        }
      }
      if (hlsRef.current) {
        hlsRef.current.subtitleTrack = -1;
      }
      if (dashRef.current) {
        const textTracks = dashRef.current.getTracksFor('text');
        if (textTracks && textTracks.length > 0) {
          dashRef.current.setTextTrack(-1);
        }
      }
    } else {
      // Enable subtitles
      dispatch(setSubtitlesEnabled(true));
      
      // For HLS
      if (hlsRef.current && hlsRef.current.subtitleTracks.length > 0) {
        const defaultTrack = hlsRef.current.subtitleTracks[0];
        hlsRef.current.subtitleTrack = defaultTrack.id;
        dispatch(setCurrentSubtitle(defaultTrack.name || defaultTrack.lang || 'unknown'));
      }
      // For DASH
      else if (dashRef.current) {
        const textTracks = dashRef.current.getTracksFor('text');
        if (textTracks && textTracks.length > 0) {
          dashRef.current.setTextTrack(0);
          dispatch(setCurrentSubtitle(textTracks[0].lang || 'unknown'));
        }
      }
      // For native tracks
      else if (tracks && tracks.length > 0) {
        for (let i = 0; i < tracks.length; i++) {
          if (i === 0) {
            tracks[i].mode = 'showing';
            dispatch(setCurrentSubtitle(tracks[i].language || 'unknown'));
          } else {
            tracks[i].mode = 'disabled';
          }
        }
      }
    }
  };

  // Add subtitle track change monitoring
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTracksChange = () => {
      const tracks = video.textTracks;
      if (tracks.length > 0) {
        const availableTracks = Array.from(tracks).map(track => ({
          id: track.id || '0',
          language: track.language || 'unknown',
          label: track.label || track.language || 'Unknown',
          mode: track.mode
        }));
        dispatch(setAvailableSubtitles(availableTracks));
      }
    };

    video.textTracks.addEventListener('addtrack', handleTracksChange);
    video.textTracks.addEventListener('removetrack', handleTracksChange);

    return () => {
      video.textTracks.removeEventListener('addtrack', handleTracksChange);
      video.textTracks.removeEventListener('removetrack', handleTracksChange);
    };
  }, [dispatch]);

  // Monitor HLS subtitle track changes
  useEffect(() => {
    if (hlsRef.current) {
      const hls = hlsRef.current;
      
      const handleSubtitleTracksUpdated = () => {
        if (subtitlesEnabled && hls.subtitleTracks.length > 0) {
          const currentTrack = hls.subtitleTracks[hls.subtitleTrack];
          if (currentTrack) {
            dispatch(setCurrentSubtitle(currentTrack.name || currentTrack.lang || 'unknown'));
          }
        }
      };

      hls.on(Hls.Events.SUBTITLE_TRACKS_UPDATED, handleSubtitleTracksUpdated);
      hls.on(Hls.Events.SUBTITLE_TRACK_SWITCH, handleSubtitleTracksUpdated);

      return () => {
        hls.off(Hls.Events.SUBTITLE_TRACKS_UPDATED, handleSubtitleTracksUpdated);
        hls.off(Hls.Events.SUBTITLE_TRACK_SWITCH, handleSubtitleTracksUpdated);
      };
    }
  }, [dispatch, subtitlesEnabled]);

  // Add after the other useEffect hooks
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Only handle shortcuts if video element exists
      if (!videoRef.current) return;

      // Don't trigger shortcuts if user is typing in an input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;

      switch (e.key.toLowerCase()) {
        case ' ':  // Space
          e.preventDefault(); // Prevent page scroll
          if (videoRef.current.paused) {
            videoRef.current.play().catch(console.error);
          } else {
            videoRef.current.pause();
          }
          break;
        case 'f':  // Fullscreen
          e.preventDefault();
          if (containerRef.current) {
            if (!document.fullscreenElement) {
              containerRef.current.requestFullscreen().then(() => {
                dispatch(setFullscreen(true));
              }).catch(console.error);
            } else {
              document.exitFullscreen().then(() => {
                dispatch(setFullscreen(false));
              }).catch(console.error);
            }
          }
          break;
        case 'm':  // Mute
          e.preventDefault();
          if (videoRef.current) {
            videoRef.current.muted = !videoRef.current.muted;
          }
          break;
        case 'arrowup':  // Volume up
          e.preventDefault();
          if (videoRef.current) {
            const newVolume = Math.min(1, videoRef.current.volume + 0.1);
            videoRef.current.volume = newVolume;
          }
          break;
        case 'arrowdown':  // Volume down
          e.preventDefault();
          if (videoRef.current) {
            const newVolume = Math.max(0, videoRef.current.volume - 0.1);
            videoRef.current.volume = newVolume;
          }
          break;
        case 'arrowleft':  // Seek backward
          e.preventDefault();
          if (videoRef.current) {
            videoRef.current.currentTime = Math.max(0, videoRef.current.currentTime - 5);
          }
          break;
        case 'arrowright':  // Seek forward
          e.preventDefault();
          if (videoRef.current) {
            videoRef.current.currentTime = Math.min(
              videoRef.current.duration,
              videoRef.current.currentTime + 5
            );
          }
          break;
      }
    };

    // Add event listener
    document.addEventListener('keydown', handleKeyPress);

    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [dispatch]);

  const formatTime = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    if (h > 0) {
      return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    }
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      setProgress(video.currentTime);
    };

    const handleDurationChange = () => {
      setDuration(video.duration);
    };

    const handleProgress = () => {
      if (video.buffered.length > 0) {
        setBuffered(video.buffered.end(video.buffered.length - 1));
      }
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('durationchange', handleDurationChange);
    video.addEventListener('progress', handleProgress);

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('durationchange', handleDurationChange);
      video.removeEventListener('progress', handleProgress);
    };
  }, []);

  // Update handleSeekDebounced function
  const handleSeekDebounced = (newTime: number) => {
    if (seekDebounceRef.current) {
      clearTimeout(seekDebounceRef.current);
    }
    
    // Start seeking state immediately
    dispatch(setSeekingState({ 
      isSeeking: true,
      startTime: Date.now(),
      targetTime: newTime
    }));
    
    // Update UI immediately
    setProgress(newTime);
    
    // Set timeout to show loading if seek takes longer than 100ms
    if (seekingTimeoutRef.current) {
      clearTimeout(seekingTimeoutRef.current);
    }
    seekingTimeoutRef.current = setTimeout(() => {
      if (videoRef.current && Math.abs(videoRef.current.currentTime - newTime) > 0.5) {
        setIsLoading(true);
      }
    }, 100);
    
    // Debounce actual seeking
    seekDebounceRef.current = setTimeout(() => {
      if (videoRef.current && Math.abs(videoRef.current.currentTime - newTime) > 0.5) {
        videoRef.current.currentTime = newTime;
      }
    }, 150);
  };

  // Update handleTouchMove
  const handleTouchMove = (e: TouchEvent) => {
    if (isDragging && progressBarRef.current) {
      e.preventDefault();
      const touch = e.touches[0];
      const time = getTimeFromPosition(touch.clientX);
      handleSeekDebounced(time);
    }
  };

  // Update mouse move effect
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging && progressBarRef.current) {
        e.preventDefault();
        const time = getTimeFromPosition(e.clientX);
        handleSeekDebounced(time);
      }
    };

    const handleMouseUp = () => {
      if (isDragging && videoRef.current) {
        // Final seek
        videoRef.current.currentTime = progress;
        // Clear seeking state after a short delay
        setTimeout(() => {
          dispatch(setSeekingState({ isSeeking: false }));
          setIsLoading(false);
        }, 200);
      }
      setIsDragging(false);
    };

    const handleTouchEnd = () => {
      if (isDragging && videoRef.current) {
        // Final seek
        videoRef.current.currentTime = progress;
        // Clear seeking state after a short delay
        setTimeout(() => {
          dispatch(setSeekingState({ isSeeking: false }));
          setIsLoading(false);
        }, 200);
      }
      setIsDragging(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, progress, dispatch]);

  // Handle mouse/touch position to time conversion
  const getTimeFromPosition = (clientX: number): number => {
    if (!progressBarRef.current || !duration) return 0;
    
    const rect = progressBarRef.current.getBoundingClientRect();
    const position = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return position * duration;
  };

  // Double click handling for seeking
  const handleVideoDoubleClick = (e: React.MouseEvent<HTMLVideoElement>) => {
    if (!videoRef.current) return;
    
    const rect = videoRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const threshold = rect.width / 2;
    
    // Seek forward if clicked on right half, backward if clicked on left half
    if (clickX > threshold) {
      videoRef.current.currentTime = Math.min(videoRef.current.duration, videoRef.current.currentTime + 10);
    } else {
      videoRef.current.currentTime = Math.max(0, videoRef.current.currentTime - 10);
    }
  };

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!videoRef.current) return;
      
      // Ignore if typing in input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;

      const video = videoRef.current;
      const smallSeek = 5;  // 5 seconds
      const largeSeek = 10; // 10 seconds
      
      switch(e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          video.currentTime = Math.max(0, video.currentTime - (e.shiftKey ? largeSeek : smallSeek));
          break;
        case 'ArrowRight':
          e.preventDefault();
          video.currentTime = Math.min(video.duration, video.currentTime + (e.shiftKey ? largeSeek : smallSeek));
          break;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Add settings-related effects
  useEffect(() => {
    if (!videoRef.current) return;
    videoRef.current.playbackRate = playbackSpeed;
  }, [playbackSpeed]);

  useEffect(() => {
    if (hlsRef.current) {
      const hls = hlsRef.current;
      logger.debug('HLS Quality Levels:', {
        levels: hls.levels.map(level => ({
          height: level.height,
          width: level.width,
          bitrate: level.bitrate,
          name: level.name,
          url: level.url
        }))
      });
      
      const levels = hls.levels.map(level => 
        `${level.height}p`
      );
      logger.debug('Setting available qualities (HLS):', levels);
      dispatch(setAvailableQualities(levels));

      // Add quality level change handler
      hls.on(Hls.Events.LEVEL_SWITCHED, (event: any, data: any) => {
        const newLevel = hls.levels[data.level];
        if (newLevel) {
          dispatch(setCurrentActiveQuality(`${newLevel.height}p`));
          logger.debug('HLS quality switched:', {
            newQuality: `${newLevel.height}p`,
            bitrate: newLevel.bitrate,
            level: data.level
          });
          
          // Update stats with the new bitrate - fix by dividing by 1000 to get kbps
          setStats(prev => ({
            ...prev,
            bitrate: newLevel.bitrate / 1000,
            level: `${data.level}`
          }));
        }
      });
    } else if (dashRef.current) {
      const dash = dashRef.current;
      const bitrateInfo = dash.getBitrateInfoListFor('video');
      logger.debug('DASH Quality Levels:', {
        levels: bitrateInfo.map(info => ({
          height: info.height,
          width: info.width,
          bitrate: info.bitrate,
          qualityIndex: info.qualityIndex
        }))
      });
      
      const qualities = bitrateInfo.map(info => 
        `${info.height}p`
      );
      logger.debug('Setting available qualities (DASH):', qualities);
      dispatch(setAvailableQualities(qualities));
    }
  }, [dispatch]);

  // Add quality change effect
  useEffect(() => {
    if (!selectedQuality || !hlsRef.current || autoQuality) return;

    const hls = hlsRef.current;
    const qualityLevel = hls.levels.findIndex(
      level => `${level.height}p` === selectedQuality
    );

    logger.debug('Manual quality change:', {
      selectedQuality,
      availableLevels: hls.levels.map(l => `${l.height}p`),
      foundLevel: qualityLevel,
      currentLevel: hls.currentLevel
    });

    if (qualityLevel !== -1) {
      hls.currentLevel = qualityLevel;
    }
  }, [selectedQuality, autoQuality]);

  useEffect(() => {
    if (!autoQuality || !hlsRef.current) return;

    const hls = hlsRef.current;
    hls.currentLevel = -1; // -1 means auto
  }, [autoQuality]);

  // Add DASH quality handling
  useEffect(() => {
    if (!selectedQuality || !dashRef.current || autoQuality) return;

    const dash = dashRef.current;
    const qualities = dash.getBitrateInfoListFor('video');
    const qualityLevel = qualities.findIndex(
      quality => `${quality.height}p` === selectedQuality
    );

    if (qualityLevel !== -1) {
      dash.setQualityFor('video', qualityLevel);
    }
  }, [selectedQuality, autoQuality]);

  useEffect(() => {
    if (!autoQuality || !dashRef.current) return;

    const dash = dashRef.current;
    dash.updateSettings({
      streaming: {
        abr: {
          autoSwitchBitrate: {
            video: true
          }
        }
      }
    });
  }, [autoQuality]);

  // Add effect to handle settings menu visibility
  useEffect(() => {
    if (settingsOpen) {
      setControlsVisible(true);
    } else {
      // Reset controls visibility after a short delay when settings are closed
      const timer = setTimeout(() => {
        if (!isHovering && !isMobile) {
          setControlsVisible(false);
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [settingsOpen, isHovering, isMobile]);

  const handleVideoTouchEnd = (e: React.TouchEvent) => {
    if (isMobile) {
      e.preventDefault();
      e.stopPropagation();
      
      // Show tap indicator
      const rect = e.currentTarget.getBoundingClientRect();
      const touch = e.changedTouches[0];
      setTapIndicator({
        x: touch.clientX - rect.left,
        y: touch.clientY - rect.top
      });

      // Clear tap indicator after animation
      setTimeout(() => {
        setTapIndicator(null);
      }, 400);

      // Toggle controls visibility
      setControlsVisible(prev => !prev);
    }
  };

  const handleControlsTouchEnd = (e: React.TouchEvent) => {
    // Only prevent default for control interactions
    e.preventDefault();
    e.stopPropagation();
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    // Remove auto-show behavior
  };

  // Add seeking event handlers
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleSeeking = () => {
      logger.debug('Video seeking started');
      dispatch(setSeekingState({ 
        isSeeking: true,
        startTime: Date.now(),
        targetTime: video.currentTime
      }));
    };

    const handleSeeked = () => {
      logger.debug('Video seeking ended');
      // Clear any existing timeout
      if (seekingTimeoutRef.current) {
        clearTimeout(seekingTimeoutRef.current);
        seekingTimeoutRef.current = null;
      }
      setIsLoading(false);
      dispatch(setSeekingState({ isSeeking: false }));
    };

    // Add protocol-specific handlers
    if (hlsRef.current) {
      hlsRef.current.on(Hls.Events.FRAG_LOADED, handleSeeked);
    } else if (dashRef.current) {
      dashRef.current.on(dashjs.MediaPlayer.events.FRAGMENT_LOADING_COMPLETED, handleSeeked);
    }

    video.addEventListener('seeking', handleSeeking);
    video.addEventListener('seeked', handleSeeked);

    return () => {
      if (seekingTimeoutRef.current) {
        clearTimeout(seekingTimeoutRef.current);
      }
      if (hlsRef.current) {
        hlsRef.current.off(Hls.Events.FRAG_LOADED, handleSeeked);
      }
      if (dashRef.current) {
        dashRef.current.off(dashjs.MediaPlayer.events.FRAGMENT_LOADING_COMPLETED, handleSeeked);
      }
      video.removeEventListener('seeking', handleSeeking);
      video.removeEventListener('seeked', handleSeeked);
    };
  }, [dispatch]);

  // Add a periodic stats update for HLS
  useEffect(() => {
    if (!hlsRef.current || !videoRef.current) return;
    
    const hls = hlsRef.current;
    const updateInterval = setInterval(() => {
      if (hls.currentLevel >= 0 && hls.levels && hls.levels.length > 0) {
        const currentLevel = hls.levels[hls.currentLevel];
        if (currentLevel && videoRef.current) {
          const video = videoRef.current;
          const bufferedTime = video.buffered && video.buffered.length > 0 ? 
            video.buffered.end(video.buffered.length - 1) - video.currentTime : 0;
          
          logger.debug('Periodic HLS stats update:', {
            bitrate: currentLevel.bitrate / 1000,
            level: hls.currentLevel,
            buffered: bufferedTime
          });
          
          setStats(prev => ({
            ...prev,
            bitrate: currentLevel.bitrate / 1000,
            level: `${hls.currentLevel}`,
            buffered: bufferedTime
          }));
        }
      }
    }, 5000);
    
    return () => clearInterval(updateInterval);
  }, []);

  // Add a periodic stats update for DASH
  useEffect(() => {
    if (!dashRef.current || !videoRef.current) return;
    
    const dash = dashRef.current;
    const updateInterval = setInterval(() => {
      const videoInfo = dash.getBitrateInfoListFor("video");
      const currentQualityIdx = dash.getQualityFor("video");
      
      if (videoInfo && videoInfo.length > 0 && currentQualityIdx >= 0 && videoRef.current) {
        const currentQuality = videoInfo[currentQualityIdx];
        const video = videoRef.current;
        
        if (currentQuality) {
          const bufferedTime = video.buffered && video.buffered.length > 0 ? 
            video.buffered.end(video.buffered.length - 1) - video.currentTime : 0;
          
          logger.debug('Periodic DASH stats update:', {
            bitrate: currentQuality.bitrate / 1000,
            level: currentQualityIdx,
            buffered: bufferedTime
          });
          
          setStats(prev => ({
            ...prev,
            bitrate: currentQuality.bitrate / 1000,
            level: `${currentQualityIdx}`,
            buffered: bufferedTime
          }));
        }
      }
    }, 5000);
    
    return () => clearInterval(updateInterval);
  }, []);

  // Add effect to update stats when playback begins
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handlePlaying = () => {
      // Update stats when playback starts
      if (hlsRef.current) {
        const hls = hlsRef.current;
        if (hls.currentLevel >= 0 && hls.levels && hls.levels.length > 0) {
          const currentLevel = hls.levels[hls.currentLevel];
          if (currentLevel) {
            logger.debug('Updating stats on playback start (HLS):', {
              bitrate: currentLevel.bitrate / 1000,
              level: hls.currentLevel
            });
            
            setStats(prev => ({
              ...prev,
              bitrate: currentLevel.bitrate / 1000,
              level: `${hls.currentLevel}`
            }));
          }
        }
      } else if (dashRef.current) {
        const dash = dashRef.current;
        const videoInfo = dash.getBitrateInfoListFor("video");
        const currentQualityIdx = dash.getQualityFor("video");
        
        if (videoInfo && videoInfo.length > 0 && currentQualityIdx >= 0) {
          const currentQuality = videoInfo[currentQualityIdx];
          if (currentQuality) {
            logger.debug('Updating stats on playback start (DASH):', {
              bitrate: currentQuality.bitrate / 1000,
              level: currentQualityIdx
            });
            
            setStats(prev => ({
              ...prev,
              bitrate: currentQuality.bitrate / 1000,
              level: `${currentQualityIdx}`
            }));
          }
        }
      }
    };

    video.addEventListener('playing', handlePlaying);
    
    return () => {
      video.removeEventListener('playing', handlePlaying);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className={`relative w-full h-full video-container bg-black ${
        isFullscreen 
          ? `fixed inset-0 z-50 fullscreen ${
              !isMobile && !controlsVisible && !settingsOpen 
                ? 'controls-hidden' 
                : ''
            }` 
          : ''
      }`}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => !isMobile && setIsHovering(true)}
      onMouseLeave={() => {
        if (!isMobile && !settingsOpen) {
          setIsHovering(false);
          setControlsVisible(false);
        }
      }}
      onTouchEnd={handleVideoTouchEnd}
    >
      <video
        ref={videoRef}
        className={`w-full h-full object-contain bg-black ${isFullscreen ? 'absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2' : ''}`}
        playsInline
        webkit-playsinline="true"
        x-webkit-airplay="allow"
        onClick={togglePlay}
        onDoubleClick={handleVideoDoubleClick}
      >
        Your browser does not support the video tag.
      </video>
      
      {/* Tap Indicator */}
      {tapIndicator && (
        <div
          className="absolute w-12 h-12 rounded-full bg-white/30 animate-ripple pointer-events-none"
          style={{
            left: tapIndicator.x - 24,
            top: tapIndicator.y - 24
          }}
        />
      )}
      
      {(isLoading || (isSeeking && isLoading)) && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50 transition-opacity duration-200">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )}
      
      <div 
        className={clsx(
          'absolute inset-0 flex items-center justify-center bg-black/20 transition-opacity duration-300',
          (isMobile 
            ? controlsVisible 
            : (isFullscreen || settingsOpen ? controlsVisible : isHovering)) ? 'opacity-100' : 'opacity-0'
        )}
      >
        <button 
          className="p-4 hover:scale-110 transition-transform"
          onClick={togglePlay}
          onTouchEnd={e => {
            e.preventDefault();
            e.stopPropagation();
            togglePlay();
          }}
        >
          {isPlaying ? (
            <Pause size={64} className="text-white/80 sm:w-16 sm:h-16 w-12 h-12" />
          ) : (
            <Play size={64} className="text-white/80 sm:w-16 sm:h-16 w-12 h-12" />
          )}
        </button>
      </div>
      
      {error && (
        <div className="absolute top-4 left-4 right-4 md:right-auto bg-red-500 text-white px-4 py-2 rounded-lg flex items-center justify-between">
          <div className="flex items-center">
            <AlertCircle className="mr-2 shrink-0" size={20} />
            <span className="text-sm">{error}</span>
          </div>
          <button
            onClick={() => setError(null)}
            onTouchEnd={(e) => {
              e.preventDefault();
              setError(null);
            }}
            className="ml-4 p-1 hover:bg-red-600 rounded-full shrink-0"
          >
            <X size={16} />
          </button>
        </div>
      )}

      <div 
        className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 transition-all duration-200 transform video-controls ${
          (isMobile 
            ? controlsVisible 
            : (isFullscreen || settingsOpen ? controlsVisible : isHovering)) ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
        } z-[9998]`}
        onClick={e => e.stopPropagation()}
      >
        <div className="space-y-2" onTouchEnd={e => e.stopPropagation()}>
          <div 
            ref={progressBarRef}
            className="relative group cursor-pointer touch-none"
            onMouseDown={(e) => {
              setIsDragging(true);
              const time = getTimeFromPosition(e.clientX);
              handleSeekDebounced(time);
            }}
            onTouchStart={(e) => {
              setIsDragging(true);
              const touch = e.touches[0];
              const time = getTimeFromPosition(touch.clientX);
              handleSeekDebounced(time);
            }}
          >
            {/* Touch Timestamp Tooltip */}
            {isDragging && (
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 bg-black/80 text-white text-xs px-2 py-1 rounded pointer-events-none">
                {formatTime(progress)}
              </div>
            )}

            {/* Buffered Progress */}
            <div className="absolute h-1 sm:h-1.5 bg-gray-600/50 rounded-full transition-all group-hover:h-2 sm:group-hover:h-3" 
                 style={{ width: `${(buffered / duration) * 100}%` }} />
            
            {/* Playback Progress */}
            <div className="absolute h-1 sm:h-1.5 bg-primary rounded-full transition-all group-hover:h-2 sm:group-hover:h-3" 
                 style={{ width: `${(progress / duration) * 100}%` }} />
            
            {/* Seek Bar Background */}
            <div className="h-2 sm:h-3 bg-transparent group-hover:bg-gray-600/30 rounded-full transition-all" />

            {/* Touch-friendly hit area */}
            <div className="absolute inset-0 -my-2 sm:-my-3" />
          </div>

          <div className="flex items-center gap-6">
            {/* Group 1: Volume Controls */}
            <div className="flex items-center gap-3">
              <button
                onClick={toggleMute}
                onTouchEnd={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleMute(e as any);
                }}
                className="text-white hover:text-gray-200"
                type="button"
              >
                {isMuted ? <VolumeX size={24} /> : <Volume2 size={24} />}
              </button>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={volume}
                onChange={handleVolumeChange}
                className="w-24 hidden sm:block"
              />
            </div>

            {/* Group 2: Time and Bitrate */}
            <div className="flex-1 flex items-center">
              {/* Duration Display */}
              <div className="text-white text-sm">
                {formatTime(progress)} / {formatTime(duration)}
              </div>
              
              {/* Bitrate Display - Desktop Only */}
              <div className="hidden sm:flex items-center text-white text-sm ml-6">
                <Signal size={16} className="mr-1" />
                {stats.bitrate > 0 ? (
                  <span>
                    {stats.bitrate > 1000 
                      ? `${(stats.bitrate / 1024).toFixed(1)} Mbps` 
                      : `${Math.round(stats.bitrate)} Kbps`}
                  </span>
                ) : (
                  <span>-- Kbps</span>
                )}
              </div>
            </div>

            {/* Group 3: Utility Controls */}
            <div className="flex items-center gap-4">
              <button
                onClick={handleSubtitleToggle}
                onTouchEnd={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSubtitleToggle(e as any);
                  logger.debug('Subtitle button clicked');
                }}
                className={`text-white hover:text-gray-200 ${subtitlesEnabled ? 'text-blue-400' : ''}`}
                type="button"
                title="Toggle Subtitles"
              >
                <Subtitles size={24} />
              </button>

              <Settings isMobile={isMobile} containerRef={containerRef} />

              <button
                onClick={togglePiP}
                onTouchEnd={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  togglePiP(e as any);
                  logger.debug('PiP button clicked');
                }}
                className={`text-white hover:text-gray-200 ${isPiP ? 'text-blue-400' : ''}`}
                title="Picture in Picture"
                type="button"
              >
                <PictureInPicture2 size={24} />
              </button>

              <button
                onClick={toggleFullscreen}
                onTouchEnd={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleFullscreen(e as any);
                  logger.debug('Fullscreen button clicked');
                }}
                className="text-white hover:text-gray-200"
                type="button"
              >
                <Maximize size={24} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Add to your CSS (tailwind.config.js or global CSS)
/*
@keyframes ripple {
  0% {
    transform: scale(0);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 0;
  }
}

.animate-ripple {
  animation: ripple 0.4s ease-out forwards;
}
*/